import type { ContextMenuItem } from "@afar-research/contracts";

import { writeTextToClipboard } from "../../hooks/useCopyToClipboard";
import { readLocalApi } from "../../localApi";
import { toastManager } from "../ui/toast";

export type PullRequestLinkContextMenuAction = "copy-link" | "open-external";

/** Named for where the reader will land, rather than the vaguer "externally". */
export const OPEN_ON_HOST_LABEL = "Open on GitHub";

/** Copy first: it is the reason to right-click a number rather than click it. */
function pullRequestLinkContextMenuItems(): ReadonlyArray<
  ContextMenuItem<PullRequestLinkContextMenuAction>
> {
  return [
    { id: "copy-link", label: "Copy link", icon: "copy" },
    { id: "open-external", label: OPEN_ON_HOST_LABEL },
  ];
}

/**
 * The right-click on a pull request's number. Everywhere that number is written it is a link,
 * and the gesture that copies a link is the one a hand reaches for — so without this the
 * platform's own edit menu opens over a control that has nothing to cut, paste or select.
 */
export async function showPullRequestLinkContextMenu({
  url,
  position,
}: {
  readonly url: string;
  readonly position: { readonly x: number; readonly y: number };
}): Promise<void> {
  const api = readLocalApi();
  if (!api) return;
  let action: PullRequestLinkContextMenuAction | null = null;
  try {
    action = await api.contextMenu.show(pullRequestLinkContextMenuItems(), position);
  } catch {
    // A menu that could not be shown has already cost the reader their right-click; a second
    // popup saying so only makes it worse.
    return;
  }
  try {
    if (action === "copy-link") await writeTextToClipboard(url, "link");
    else if (action === "open-external") await api.shell.openExternal(url);
  } catch {
    toastManager.add({
      type: "error",
      title: action === "copy-link" ? "Could not copy the link" : "Could not open the link",
    });
  }
}
